import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
} from '@angular/core';

type Axis = 'horizontal' | 'vertical';

@Directive({
  selector: '[pdvTableScrollbar]',
})
export class TableScrollbarDirective implements AfterViewInit, OnDestroy {
  private scrollElement?: HTMLElement;
  private readonly cleanup: Array<() => void> = [];
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    requestAnimationFrame(() => this.initialize());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.cleanup.forEach((listener) => listener());
  }

  private initialize(): void {
    const scrollElement = this.host.nativeElement.querySelector<HTMLElement>(
      '.table-scroll-container, .auto-size, .pdv-scrollbar-viewport',
    );

    if (!scrollElement) return;

    this.scrollElement = scrollElement;
    this.host.nativeElement.classList.add('pdv-table-scrollbar');
    scrollElement.classList.add('pdv-table-scrollbar__content');

    const vertical = this.createScrollbar('vertical');
    const horizontal = this.createScrollbar('horizontal');
    this.host.nativeElement.append(vertical.root, horizontal.root);

    const update = () => {
      this.updateScrollbar(vertical, 'vertical');
      this.updateScrollbar(horizontal, 'horizontal');
    };

    scrollElement.addEventListener('scroll', update, { passive: true });
    this.cleanup.push(() => scrollElement.removeEventListener('scroll', update));

    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(scrollElement);
    this.resizeObserver.observe(this.host.nativeElement);
    this.mutationObserver = new MutationObserver(update);
    this.mutationObserver.observe(scrollElement, {
      childList: true,
      subtree: true,
    });
    update();
  }

  private createScrollbar(axis: Axis) {
    const root = document.createElement('div');
    root.className = `pdv-table-scrollbar__bar pdv-table-scrollbar__bar--${axis}`;

    const previous = this.createButton(axis, -1);
    const track = document.createElement('div');
    track.className = 'pdv-table-scrollbar__track';
    const thumb = document.createElement('div');
    thumb.className = 'pdv-table-scrollbar__thumb';
    track.appendChild(thumb);
    const next = this.createButton(axis, 1);
    root.append(previous, track, next);

    this.enableThumbDrag(thumb, track, axis);
    return { root, track, thumb };
  }

  private createButton(axis: Axis, direction: number): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pdv-table-scrollbar__button pdv-table-scrollbar__button--${direction < 0 ? 'previous' : 'next'}`;
    button.setAttribute('aria-label', `Deslocar tabela para ${direction < 0 ? (axis === 'vertical' ? 'cima' : 'a esquerda') : axis === 'vertical' ? 'baixo' : 'a direita'}`);
    button.addEventListener('click', () => {
      this.scrollElement?.scrollBy(
        axis === 'vertical' ? { top: direction * 96, behavior: 'smooth' } : { left: direction * 96, behavior: 'smooth' },
      );
    });
    this.cleanup.push(() => button.replaceWith());
    return button;
  }

  private enableThumbDrag(thumb: HTMLElement, track: HTMLElement, axis: Axis): void {
    thumb.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      const startPointer = axis === 'vertical' ? event.clientY : event.clientX;
      const startScroll = axis === 'vertical' ? this.scrollElement?.scrollTop ?? 0 : this.scrollElement?.scrollLeft ?? 0;

      const move = (moveEvent: PointerEvent) => {
        const scrollElement = this.scrollElement;
        if (!scrollElement) return;
        const pointer = axis === 'vertical' ? moveEvent.clientY : moveEvent.clientX;
        const trackSize = axis === 'vertical' ? track.clientHeight : track.clientWidth;
        const thumbSize = axis === 'vertical' ? thumb.clientHeight : thumb.clientWidth;
        const scrollSize = axis === 'vertical' ? scrollElement.scrollHeight - scrollElement.clientHeight : scrollElement.scrollWidth - scrollElement.clientWidth;
        const offset = ((pointer - startPointer) / Math.max(trackSize - thumbSize, 1)) * scrollSize;
        if (axis === 'vertical') scrollElement.scrollTop = startScroll + offset;
        else scrollElement.scrollLeft = startScroll + offset;
      };
      const end = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end, { once: true });
    });
  }

  private updateScrollbar(
    scrollbar: { root: HTMLElement; track: HTMLElement; thumb: HTMLElement },
    axis: Axis,
  ): void {
    const element = this.scrollElement;
    if (!element) return;
    const isVertical = axis === 'vertical';
    const clientSize = isVertical ? element.clientHeight : element.clientWidth;
    const scrollSize = isVertical ? element.scrollHeight : element.scrollWidth;
    const overflow = scrollSize > clientSize + 1;
    scrollbar.root.hidden = !overflow;
    if (!overflow) return;

    if (isVertical) {
      scrollbar.root.style.top = `${element.offsetTop}px`;
      scrollbar.root.style.left = `${element.offsetLeft + element.clientWidth - 18}px`;
      scrollbar.root.style.height = `${element.clientHeight}px`;
    } else {
      scrollbar.root.style.top = `${element.offsetTop + element.clientHeight - 18}px`;
      scrollbar.root.style.left = `${element.offsetLeft}px`;
      scrollbar.root.style.width = `${element.clientWidth - 18}px`;
    }

    const trackSize = isVertical ? scrollbar.track.clientHeight : scrollbar.track.clientWidth;
    const thumbSize = Math.max(32, (clientSize / scrollSize) * trackSize);
    const scrollPosition = isVertical ? element.scrollTop : element.scrollLeft;
    const maxScroll = scrollSize - clientSize;
    const thumbPosition = (scrollPosition / maxScroll) * (trackSize - thumbSize);
    const positionProperty = isVertical ? 'top' : 'left';
    const sizeProperty = isVertical ? 'height' : 'width';
    scrollbar.thumb.style.setProperty(sizeProperty, `${thumbSize}px`);
    scrollbar.thumb.style.setProperty(positionProperty, `${thumbPosition}px`);
  }
}
